import React, { useEffect, useState } from "react"
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import PageHeader from "../../../components/DashboardComponents/PageHeader"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import { ICONS } from "../../../constants/icons"
import { get, post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { getApiFieldErrors } from "../../../helpers/formFieldErrors"
import {
  ARTS_GENOME_TYPES,
  FORM_CONFIG,
  mapRoleClassRow,
  mapSpellbookRow,
} from "./adminArtsGenomeData"
import UploadBulkModal from "./UploadBulkModal"
import KeywordMappingForm from "./KeywordMappingForm"

const FORM_ID = "admin-arts-genome-form"

const NAMED_ENTITY_CONFIG = {
  spellbook: {
    listEndpoint: endpoints.getAdminSpellbooks,
    createEndpoint: endpoints.createAdminSpellbooks,
    updateEndpoint: endpoints.updateAdminSpellbook,
    mapRow: mapSpellbookRow,
    navStateKey: "spellbook",
    loadError: "Failed to load spellbook",
    notFound: "Spellbook not found.",
    createFail: "Failed to create spellbook",
    updateFail: "Failed to update spellbook",
    ariaLabel: "Loading spellbook form",
  },
  class: {
    listEndpoint: endpoints.getAdminRoleClasses,
    createEndpoint: endpoints.createAdminRoleClasses,
    updateEndpoint: endpoints.updateAdminRoleClass,
    mapRow: mapRoleClassRow,
    navStateKey: "roleClass",
    loadError: "Failed to load class type",
    notFound: "Class type not found.",
    createFail: "Failed to create class type",
    updateFail: "Failed to update class type",
    ariaLabel: "Loading class type form",
  },
}

/**
 * Create / Edit Arts Genome named entity (spellbook / class).
 */
const ArtsGenomeForm = ({ mode = "create" }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { type, itemId } = useParams()
  const isEdit = mode === "edit"
  const formConfig = FORM_CONFIG[type]
  const entityConfig = NAMED_ENTITY_CONFIG[type]
  const [name, setName] = useState("")
  const [saving, setSaving] = useState(false)
  const [nameError, setNameError] = useState("")
  const [bulkModalOpen, setBulkModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(
    () => Boolean(entityConfig) && isEdit,
  )
  const [loadError, setLoadError] = useState(null)
  const [entityItem, setEntityItem] = useState(null)

  useEffect(() => {
    if (!formConfig) return
    document.title = `${isEdit ? formConfig.editTitle : formConfig.createTitle} | AMOK . EARTH Admin`
  }, [formConfig, isEdit])

  useEffect(() => {
    if (!entityConfig) return undefined

    let cancelled = false

    const loadEntity = async () => {
      if (!isEdit) {
        setName("")
        setEntityItem(null)
        setIsLoading(false)
        setLoadError(null)
        return
      }

      setIsLoading(true)
      setLoadError(null)

      const fromNav = location.state?.[entityConfig.navStateKey]
      if (fromNav?.id && String(fromNav.id) === String(itemId)) {
        const mapped = entityConfig.mapRow(fromNav)
        if (!cancelled) {
          setEntityItem(mapped)
          setName(mapped.name || "")
          setIsLoading(false)
        }
        return
      }

      try {
        const response = await get(entityConfig.listEndpoint, {
          params: { page: 1, page_size: 100 },
        })
        if (cancelled) return

        if (!response?.data?.success) {
          setLoadError(toastApiError(response, entityConfig.loadError))
          setIsLoading(false)
          return
        }

        const found = (response.data.result?.data || []).find(
          item => String(item.id) === String(itemId),
        )
        if (!found) {
          setLoadError(entityConfig.notFound)
          setIsLoading(false)
          return
        }

        const mapped = entityConfig.mapRow(found)
        setEntityItem(mapped)
        setName(mapped.name || "")
      } catch (err) {
        if (!cancelled) {
          setLoadError(toastApiError(err, entityConfig.loadError))
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadEntity()
    return () => {
      cancelled = true
    }
  }, [entityConfig, isEdit, itemId, location.state])

  if (!ARTS_GENOME_TYPES.includes(type) || !formConfig) {
    return <Navigate to="/admin/arts-genome" replace />
  }

  if (type === "keyword") {
    return <KeywordMappingForm mode={mode} />
  }

  if (!entityConfig) {
    return <Navigate to="/admin/arts-genome" replace />
  }

  if (isEdit && !isLoading && !entityItem && loadError) {
    return (
      <Navigate
        to="/admin/arts-genome"
        replace
        state={{ tab: formConfig.listTab }}
      />
    )
  }

  if (isLoading) {
    return (
      <div
        className="admin-page admin-arts-genome-form"
        aria-busy="true"
        aria-label={entityConfig.ariaLabel}
      >
        <div className="admin-arts-genome-form__header admin-arts-genome-form__header--skeleton">
          <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--title" />
          <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--subtitle" />
        </div>
        <div className="admin-arts-genome-form__fields">
          <div className="admin-arts-genome-form__skeleton admin-arts-genome-form__skeleton--field" />
        </div>
      </div>
    )
  }

  const handleSave = async event => {
    event.preventDefault()

    if (!name.trim()) {
      setNameError(`${formConfig.fieldLabel} is required.`)
      return
    }
    setNameError("")

    setSaving(true)
    try {
      const trimmed = name.trim()
      const response = isEdit
        ? await post(
            entityConfig.updateEndpoint(itemId),
            { name: trimmed },
            { skipErrorToast: true },
          )
        : await post(
            entityConfig.createEndpoint,
            { names: [trimmed] },
            { skipErrorToast: true },
          )

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        const fieldErrors = getApiFieldErrors(response, { names: "name" })
        if (fieldErrors.name) {
          setNameError(fieldErrors.name)
          return
        }
        toastApiError(
          response,
          isEdit ? entityConfig.updateFail : entityConfig.createFail,
        )
        return
      }

      toast.success(
        response?.data?.message ||
          (isEdit
            ? `“${trimmed}” updated successfully.`
            : `“${trimmed}” saved successfully.`),
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      navigate("/admin/arts-genome", {
        state: { tab: formConfig.listTab },
      })
    } catch (err) {
      const fieldErrors = getApiFieldErrors(err, { names: "name" })
      if (fieldErrors.name) {
        setNameError(fieldErrors.name)
      } else {
        toastApiError(
          err,
          isEdit ? entityConfig.updateFail : entityConfig.createFail,
        )
      }
    } finally {
      setSaving(false)
    }
  }

  const handleBulkUpload = async names => {
    const response = await post(
      entityConfig.createEndpoint,
      { names },
      { skipErrorToast: true },
    )

    if (
      !response ||
      response.status >= 400 ||
      response?.data?.success === false
    ) {
      throw response
    }

    toast.success(
      response?.data?.message ||
        `${names.length} ${
          names.length === 1 ? "item" : "items"
        } uploaded successfully.`,
      {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      },
    )
    setBulkModalOpen(false)
    navigate("/admin/arts-genome", {
      state: { tab: formConfig.listTab },
    })
  }

  return (
    <div className="admin-page admin-arts-genome-form">
      <PageHeader
        className="admin-arts-genome-form__header"
        title={isEdit ? formConfig.editTitle : formConfig.createTitle}
        subtitle={
          isEdit ? formConfig.editSubtitle : formConfig.createSubtitle
        }
        actions={
          <>
            {!isEdit && formConfig.showUploadBulk ? (
              <CustomButton
                variant="secondary"
                size="md"
                type="button"
                onClick={() => setBulkModalOpen(true)}
                disabled={saving}
              >
                Upload Bulk
              </CustomButton>
            ) : null}
            <CustomButton
              variant="primary"
              size="md"
              type="submit"
              form={FORM_ID}
              loading={saving}
              disabled={saving}
              trailingIconSrc={ICONS.arrowRight12}
              trailingIconAlt=""
            >
              {formConfig.saveLabel}
            </CustomButton>
          </>
        }
      />

      <form
        id={FORM_ID}
        className="admin-arts-genome-form__form"
        onSubmit={handleSave}
      >
        <div className="admin-arts-genome-form__fields">
          <CustomInput
            label={formConfig.fieldLabel}
            name="name"
            required
            placeholder={formConfig.fieldPlaceholder}
            value={name}
            onChange={event => {
              setName(event.target.value)
              if (nameError) setNameError("")
            }}
            error={nameError}
          />
        </div>
      </form>

      {!isEdit && formConfig.showUploadBulk ? (
        <UploadBulkModal
          isOpen={bulkModalOpen}
          onClose={() => setBulkModalOpen(false)}
          onUpload={handleBulkUpload}
          columnLabel={formConfig.bulkColumnLabel}
          failMessage={formConfig.bulkFail}
          templateExamples={formConfig.bulkTemplateExamples}
          templateFilename={formConfig.bulkTemplateFilename}
        />
      ) : null}
    </div>
  )
}

export default ArtsGenomeForm
